import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { BokehPass } from 'three/addons/postprocessing/BokehPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

/* ============================================================================
   AGENTSEE — cinematic post chain.  Warm dusk interior, lamp-lit, film frame.

   Order (and why):
     RenderPass   → into a MULTISAMPLED half-float HDR target.  MSAA is the
                    anti-aliasing: it resolves geometry silhouettes only, so the
                    white text decals painted on the floor keep every pixel of
                    their texture.  FXAA was rejected outright — it is a
                    luminance-edge filter and smears exactly that kind of thin
                    high-contrast lettering.  SMAA would survive the text but
                    costs two more full-res half-float targets for a result MSAA
                    already gives inside the render target.
     BokehPass    → defocus BEFORE bloom.  An out-of-focus lamp should bloom as a
                    big soft disc, not a sharp core smeared afterwards.
     UnrealBloom  → linear HDR, so the threshold is a real scene-luminance cut.
     grade        → lift/gamma/gain + saturation + split-tone, still in linear
                    HDR so highlights roll off through the tonemapper instead of
                    clipping under the grade.
     OutputPass   → ACES + sRGB.  The display transform.
     film         → ONE pass: chromatic aberration, vignette, grain.  Display
                    space, because grain and vignette are print/lens artefacts
                    that live on the final image, not on scene radiance.
   ========================================================================== */

/* Per-level knobs.  pixelRatio is a CAP applied to devicePixelRatio. */
const QUALITY = {
  cinematic: {
    pixelRatio: 2, samples: 4,
    dof:   { enabled: true,  aperture: 0.00072, maxblur: 0.0170 },
    bloom: { strength: 0.22, radius: 0.58, threshold: 0.38, smoothWidth: 0.52 },
    grade: true,
    film:  { enabled: true, aberration: 0.0012, grain: 0.045, vignette: 0.20 }
  },
  balanced: {
    pixelRatio: 1.5, samples: 2,
    dof:   { enabled: true,  aperture: 0.00050, maxblur: 0.0110 },
    bloom: { strength: 0.20, radius: 0.50, threshold: 0.44, smoothWidth: 0.46 },
    grade: true,
    film:  { enabled: true, aberration: 0.0006, grain: 0.035, vignette: 0.16 }
  },
  fast: {                                    // first-person walking: render + bloom + output, nothing else
    pixelRatio: 1.25, samples: 0,
    dof:   { enabled: false, aperture: 0.00030, maxblur: 0.0040 },
    bloom: { strength: 0.16, radius: 0.42, threshold: 0.62, smoothWidth: 0.30 },
    grade: false,
    film:  { enabled: false, aberration: 0.0, grain: 0.0, vignette: 0.0 }
  }
};

const FOCUS_OMEGA   = 5.2;    // rad/s, critically damped — a rack settles in ~0.8s
const APERTURE_RATE = 4.5;    // deep-focus iris fade, 1/s
const APERTURE_ZERO = 2e-5;   // below this the circle of confusion is sub-pixel — call it shut
const GRAIN_HZ      = 24;     // grain re-seeds on a 24fps cadence, not the display rate
const EXCLUDE_EVERY = 0.75;   // s between rescans for runtime-spawned sprites
const MAX_STEP      = 1 / 20; // a tab-switch spike must not fling the focus spring

/* ---------- grade: linear HDR in, linear HDR out ---------- */
const GradeShader = {
  name: 'AgentseeGrade',
  uniforms: {
    tDiffuse:      { value: null },
    exposure:      { value: 1.0 },
    contrast:      { value: 1.12 },
    saturation:    { value: 1.06 },
    lift:          { value: new THREE.Vector3(0.016, 0.010, 0.006) },  // warm, raised blacks — a print toe
    gamma:         { value: new THREE.Vector3(1.00, 0.99, 0.96) },
    gain:          { value: new THREE.Vector3(1.03, 1.00, 0.96) },
    shadowTint:    { value: new THREE.Color(1.06, 1.00, 0.92) },       // lamp bounce fills the dark
    highlightTint: { value: new THREE.Color(0.97, 0.99, 1.05) },       // dusk through the glass
    splitPivot:    { value: 0.22 },
    splitStrength: { value: 0.55 }
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */`
    varying vec2 vUv;
    uniform sampler2D tDiffuse;
    uniform float exposure, contrast, saturation, splitPivot, splitStrength;
    uniform vec3 lift, gamma, gain, shadowTint, highlightTint;

    const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

    void main(){
      vec4 texel = texture2D(tDiffuse, vUv);
      vec3 c = max(texel.rgb, 0.0) * exposure;

      // ASC-CDL slope/offset/power. The lift term is rolled off by c/(1+c) so it
      // opens the shadows without hazing a 10.0-linear lamp filament.
      c = c * gain + lift * (1.0 - c / (1.0 + c));
      c = pow(max(c, 0.0), 1.0 / max(gamma, vec3(1e-3)));
      c = max((c - 0.18) * contrast + 0.18, 0.0);        // pivot on scene-linear mid grey

      float l = dot(c, LUMA);
      c = mix(vec3(l), c, saturation);

      vec3 tint = mix(shadowTint, highlightTint, smoothstep(0.0, splitPivot * 2.0, l));
      c *= mix(vec3(1.0), tint, splitStrength);

      gl_FragColor = vec4(c, texel.a);
    }`
};

/* ---------- film: CA + vignette + grain, one pass, display space ---------- */
const FilmicShader = {
  name: 'AgentseeFilm',
  uniforms: {
    tDiffuse:       { value: null },
    resolution:     { value: new THREE.Vector2(1, 1) },
    aberration:     { value: 0.0012 },   // corner offset ≈ 1.7px on a 1920-wide frame
    // index.html already paints a CSS #vignette at 58% — these two COMPOUND, so this one
    // stays light. Zero one of them if the corners still crush.
    vignette:       { value: 0.20 },
    vignetteRadius: { value: 0.52 },     // fraction of the corner radius that stays clean
    grain:          { value: 0.045 },
    grainSize:      { value: 1.35 },     // device px per grain cell
    grainLumaBias:  { value: 0.70 },
    seed:           { value: 0.0 }
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */`
    varying vec2 vUv;
    uniform sampler2D tDiffuse;
    uniform vec2 resolution;
    uniform float aberration, vignette, vignetteRadius, grain, grainSize, grainLumaBias, seed;

    const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

    float hash21(vec2 p){
      p = fract(p * vec2(443.897, 441.423));
      p += dot(p, p.yx + 19.19);
      return fract((p.x + p.y) * p.x);
    }

    void main(){
      vec2 d = vUv - 0.5;
      vec2 a = d;
      a.x *= resolution.x / max(resolution.y, 1.0);     // fringe must be radial, not stretched by aspect

      // lateral chromatic aberration — grows with r^2, like a real uncorrected lens
      vec2 off = d * dot(a, a) * aberration;
      vec3 c = vec3(
        texture2D(tDiffuse, vUv + off).r,
        texture2D(tDiffuse, vUv).g,
        texture2D(tDiffuse, vUv - off).b
      );

      // vignette follows the frame, not a circle — 0 at centre, 1 at the corner
      float rv = length(d) * 1.41421356;
      c *= mix(1.0, smoothstep(1.0, vignetteRadius, rv), vignette);

      // grain lives in the mids: 4*l*(1-l) peaks at mid grey and dies in both the
      // blacks and the blown highlights, which is how emulsion actually behaves
      float l = dot(c, LUMA);
      float w = mix(1.0, 4.0 * l * (1.0 - l), grainLumaBias);
      float n = hash21(gl_FragCoord.xy / max(grainSize, 0.5) + seed * 137.0) - 0.5;
      c += n * grain * w;

      gl_FragColor = vec4(c, 1.0);
    }`
};

/* ---------- passes ---------- */

/* BokehPass re-renders the scene with a MeshDepthMaterial override. Two things
   poison that depth buffer and both are fixed here:
     · scene.background paints over the white clear, so open space reads NEAR
       instead of far and the void snaps into focus;
     · overrideMaterial is applied to sprites, points and lines too
       (WebGLRenderer.js:1593), so labels and additive glows stamp bogus depth
       blobs in front of whatever they float over. */
class FocusPullPass extends BokehPass {
  constructor(scene, camera, params, excludes){
    super(scene, camera, params);
    this.excludes = excludes;
  }
  render(renderer, writeBuffer, readBuffer, deltaTime, maskActive){
    const list = this.excludes.get(), was = this.excludes.state;
    for(let i = 0; i < list.length; i++){ was[i] = list[i].visible; list[i].visible = false; }
    const bg = this.scene.background; this.scene.background = null;
    super.render(renderer, writeBuffer, readBuffer, deltaTime, maskActive);
    this.scene.background = bg;
    for(let i = 0; i < list.length; i++) list[i].visible = was[i];
  }
}

/* ShaderPass has no setSize, but the composer calls it with device pixels — catching
   it here keeps the grain cell and the fringe right even when the host resizes the
   composer directly rather than going through resize(). */
class FilmicPass extends ShaderPass {
  setSize(width, height){ this.uniforms.resolution.value.set(width, height); }
}

/* Rebuilt on a timer because the floor spawns bots, trails and glows at runtime. */
function makeExcludeCache(scene){
  const list = [], state = [];
  let dirty = true;
  return {
    state,
    touch(){ dirty = true; },
    get(){
      if(!dirty) return list;
      list.length = 0;
      scene.traverse(o => {
        if(o.children.length) return;                    // never hide a node something else hangs off
        if(o.isSprite || o.isPoints || o.isLine){ list.push(o); return; }
        if(o.isMesh && o.material){
          const m = Array.isArray(o.material) ? o.material[0] : o.material;
          if(m && m.depthWrite === false) list.push(o);   // additive glows carry no real depth
        }
      });
      dirty = false;
      return list;
    }
  };
}

const pick = (user, fallback, key) => (user && user[key] !== undefined) ? user[key] : fallback[key];

/**
 * Cinematic post chain for the AGENTSEE floor.
 *
 * @param {THREE.WebGLRenderer}   renderer
 * @param {THREE.Scene}           scene
 * @param {THREE.PerspectiveCamera} camera
 * @param {object} [opts]  quality ('cinematic'|'balanced'|'fast'), pixelRatio (cap),
 *                         focusDistance, deepFocus, and per-stage overrides
 *                         dof / bloom / film / grade that survive setQuality().
 */
export function buildPost(renderer, scene, camera, opts = {}){
  const size = renderer.getSize(new THREE.Vector2());
  const userDof = opts.dof || null, userBloom = opts.bloom || null, userFilm = opts.film || null;

  let quality  = QUALITY[opts.quality] ? opts.quality : 'cinematic';
  let ratioCap = opts.pixelRatio !== undefined ? opts.pixelRatio : QUALITY[quality].pixelRatio;
  let samples  = pick(opts, QUALITY[quality], 'samples');

  const pr = () => Math.min(window.devicePixelRatio || 1, ratioCap);

  const target = new THREE.WebGLRenderTarget(size.width * pr(), size.height * pr(), {
    type: THREE.HalfFloatType,   // bloom and the grade both need headroom above 1.0
    samples                      // MSAA lives here — see the header note on AA and the floor text
  });
  target.texture.name = 'Post.hdr';

  const composer = new EffectComposer(renderer, target);
  composer.setSize(size.width, size.height);   // CSS pixels — the ctor seeded _width from the device-sized target
  composer.setPixelRatio(pr());                // then derive the effective size once, correctly

  const excludes = makeExcludeCache(scene);

  const render = new RenderPass(scene, camera);

  /* BokehShader's widest tap is 0.4 * maxblur in UV, and its 41 taps start ringing
     past maxblur ~0.02 — measured: 0.009 keeps 74% of a checkerboard's detail, 0.02
     keeps 17%, 0.05 climbs back to 22% as the kernel undersamples. 0.017 is the top
     of the clean band. aperture then sets where the clamp is reached: at 0.00072 a
     surface 24m off the focal plane is fully soft, one 6m off is only a quarter there
     — which is the falloff a 40m floor wants. */
  const dof = new FocusPullPass(scene, camera, {
    focus:    opts.focusDistance !== undefined ? opts.focusDistance : 9,
    aperture: pick(userDof, QUALITY[quality].dof, 'aperture'),
    maxblur:  pick(userDof, QUALITY[quality].dof, 'maxblur')
  }, excludes);

  /* Threshold is a scene-LINEAR luminance cut taken before the tonemapper. On this
     floor screen emissive lands near 0.5 and lamp bulbs near 1.36, so a toe at 0.38
     with a wide 0.52 smoothWidth hands the screens ~14% of their energy and the
     lamps all of theirs — glow where light is made, no milk on the lit plaster,
     which sits under 0.3. Retune the toe, not the strength. */
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(size.width * pr(), size.height * pr()),
    QUALITY[quality].bloom.strength, QUALITY[quality].bloom.radius, QUALITY[quality].bloom.threshold
  );

  const grade = new ShaderPass(GradeShader);
  if(opts.grade && typeof opts.grade === 'object') for(const k in opts.grade){
    const u = grade.uniforms[k];
    if(!u) continue;
    if(u.value && u.value.isVector3) u.value.fromArray(opts.grade[k]);
    else if(u.value && u.value.isColor) u.value.set(opts.grade[k]);
    else u.value = opts.grade[k];
  }

  const output = new OutputPass();
  const film = new FilmicPass(FilmicShader);

  composer.addPass(render);
  composer.addPass(dof);
  composer.addPass(bloom);
  composer.addPass(grade);
  composer.addPass(output);
  composer.addPass(film);

  const passes = { render, dof, bloom, grade, output, film };

  /* ---------- focus ---------- */
  const scratch = new THREE.Vector3();
  let focusTarget = null;                                        // Vector3 | Object3D | null
  let deep = !!opts.deepFocus;
  let focus = opts.focusDistance !== undefined ? opts.focusDistance : 9;
  let focusVel = 0;
  let apertureFull = pick(userDof, QUALITY[quality].dof, 'aperture');
  let aperture = deep ? 0 : apertureFull;
  let dofAllowed = QUALITY[quality].dof.enabled;
  let grainClock = 0, excludeClock = 0;

  function focusOn(where){
    if(where === null || where === undefined){ deep = true; focusTarget = null; return; }
    deep = false;
    focusTarget = where.isVector3 ? where.clone() : where;       // copy a vector, track an Object3D live
  }

  function update(dt, cameraTarget){
    const h = Math.min(Math.max(dt || 0, 0), MAX_STEP);

    let want = focus;
    if(focusTarget) want = camera.position.distanceTo(
      focusTarget.isVector3 ? focusTarget : focusTarget.getWorldPosition(scratch));
    else if(cameraTarget) want = camera.position.distanceTo(cameraTarget);

    // critically damped spring, implicit Euler: unconditionally stable, never snaps
    const hoo = h * FOCUS_OMEGA * FOCUS_OMEGA;
    const det = 1 + 2 * h * FOCUS_OMEGA + h * hoo;      // 1 + 2hw + h²w²
    focusVel = (focusVel + hoo * (want - focus)) / det;
    focus += h * focusVel;
    dof.uniforms['focus'].value = focus;

    // deep focus is a rack out, not a cut — the iris closes over about a second
    aperture += ((deep ? 0 : apertureFull) - aperture) * (1 - Math.exp(-h * APERTURE_RATE));
    if(deep && aperture < APERTURE_ZERO) aperture = 0;
    dof.uniforms['aperture'].value = aperture;
    dof.enabled = dofAllowed && aperture > 0;      // fully deep skips a whole scene depth re-render

    grainClock += h;
    if(grainClock >= 1 / GRAIN_HZ){
      grainClock %= 1 / GRAIN_HZ;
      film.uniforms.seed.value = Math.random();    // re-seed, never crawl
    }

    excludeClock += h;
    if(excludeClock >= EXCLUDE_EVERY){ excludeClock = 0; excludes.touch(); }
  }

  /* ---------- quality ---------- */
  function setQuality(level){
    if(!QUALITY[level]) level = 'balanced';
    quality = level;
    const q = QUALITY[level];

    if(opts.pixelRatio === undefined && ratioCap !== q.pixelRatio){
      ratioCap = q.pixelRatio;
      composer.setPixelRatio(pr());
      bloom.resolution.set(size.width * pr(), size.height * pr());
    }

    const wantSamples = pick(opts, q, 'samples');
    if(samples !== wantSamples){
      samples = wantSamples;
      // setupRenderTarget re-reads .samples, so a dispose is what forces the new MSAA level
      composer.renderTarget1.samples = samples; composer.renderTarget1.dispose();
      composer.renderTarget2.samples = samples; composer.renderTarget2.dispose();
    }

    dofAllowed = q.dof.enabled;
    apertureFull = pick(userDof, q.dof, 'aperture');
    dof.uniforms['maxblur'].value = pick(userDof, q.dof, 'maxblur');
    dof.enabled = dofAllowed && aperture > 1e-6;

    bloom.strength = pick(userBloom, q.bloom, 'strength');
    bloom.radius = pick(userBloom, q.bloom, 'radius');
    bloom.threshold = pick(userBloom, q.bloom, 'threshold');
    bloom.highPassUniforms['smoothWidth'].value = pick(userBloom, q.bloom, 'smoothWidth');

    grade.enabled = q.grade;

    film.enabled = pick(userFilm, q.film, 'enabled');
    film.uniforms.aberration.value = pick(userFilm, q.film, 'aberration');
    film.uniforms.grain.value = pick(userFilm, q.film, 'grain');
    film.uniforms.vignette.value = pick(userFilm, q.film, 'vignette');
  }

  /* ---------- resize: CSS pixels in, device pixels derived ---------- */
  function resize(w, h){
    size.set(w, h);
    composer.setSize(w, h);
    composer.setPixelRatio(pr());              // devicePixelRatio can change when a window moves screens
    bloom.resolution.set(w * pr(), h * pr());
    film.uniforms.resolution.value.set(w * pr(), h * pr());
  }

  function dispose(){
    composer.passes.forEach(p => p.dispose && p.dispose());
    composer.dispose();
  }

  setQuality(quality);
  dof.uniforms['focus'].value = focus;
  dof.uniforms['aperture'].value = aperture;
  film.uniforms.resolution.value.set(size.width * pr(), size.height * pr());

  return { composer, setQuality, focusOn, update, resize, passes, dispose };
}
